import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Button, Chip, IconButton, CircularProgress,
    Snackbar, Alert, Tooltip, Avatar, Skeleton, Fade, Paper,
    TablePagination, TextField, MenuItem, FormControl, InputLabel,
    Select, InputAdornment
} from '@mui/material';
import { Delete, Notifications, Search as SearchIcon } from '@mui/icons-material';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import CancelIcon from '@mui/icons-material/Cancel';
import RefreshIcon from '@mui/icons-material/Refresh';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import FilterListIcon from '@mui/icons-material/FilterList';
import axiosInstance from '../../services/axiosInstance';

// ─── Status Config ─────────────────────────────────────────────────────────────
const STATUS_CONFIG = {
    confirmed: {
        label: 'Confirmed',
        color: 'success',
        bg: '#f0fdf4',
        textColor: '#16a34a',
        borderColor: '#86efac',
        icon: <EventAvailableIcon sx={{ fontSize: 12 }} />,
    },
    pending: {
        label: 'Pending',
        color: 'warning',
        bg: '#fffbeb',
        textColor: '#d97706',
        borderColor: '#fcd34d',
        icon: <HourglassTopIcon sx={{ fontSize: 12 }} />,
    },
    cancelled: {
        label: 'Cancelled',
        color: 'error',
        bg: '#fff1f2',
        textColor: '#e11d48',
        borderColor: '#fda4af',
        icon: <CancelIcon sx={{ fontSize: 12 }} />,
    },
    completed: {
        label: 'Completed',
        color: 'secondary',
        bg: '#f5f3ff',
        textColor: '#7c3aed',
        borderColor: '#ddd6fe',
        icon: <EventAvailableIcon sx={{ fontSize: 12 }} />,
    },
};

// ─── Skeleton Rows ─────────────────────────────────────────────────────────────
const SkeletonRows = () =>
    [...Array(5)].map((_, i) => (
        <TableRow key={i}>
            {[...Array(6)].map((__, j) => (
                <TableCell key={j}>
                    <Skeleton variant="rounded" height={22} sx={{ borderRadius: 1.5 }} />
                </TableCell>
            ))}
        </TableRow>
    ));

// ─── Empty State ───────────────────────────────────────────────────────────────
const EmptyState = () => (
    <Box sx={{ textAlign: 'center', py: 8, px: 3 }}>
        <Box
            sx={{
                width: 72, height: 72, borderRadius: '50%',
                bgcolor: '#f1f5f9', display: 'flex',
                alignItems: 'center', justifyContent: 'center',
                mx: 'auto', mb: 2,
            }}
        >
            <CalendarTodayIcon sx={{ fontSize: 34, color: '#94a3b8' }} />
        </Box>
        <Typography variant="h6" fontWeight={700} color="text.primary" gutterBottom>
            No Appointments Yet
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 320, mx: 'auto' }}>
            When clients book appointments through your booking page, they'll appear here.
        </Typography>
    </Box>
);

// ─── Stats Card ────────────────────────────────────────────────────────────────
const StatCard = ({ label, count, color, bg, icon }) => (
    <Box
        sx={{
            flex: 1,
            minWidth: 110,
            bgcolor: bg,
            borderRadius: 2.5,
            px: 2, py: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            border: `1px solid ${color}33`,
            transition: 'transform 0.2s, box-shadow 0.2s',
            '&:hover': { transform: 'translateY(-2px)', boxShadow: `0 4px 16px ${color}22` },
        }}
    >
        <Box sx={{ color, fontSize: 20 }}>{icon}</Box>
        <Box>
            <Typography variant="h6" fontWeight={800} sx={{ color, lineHeight: 1 }}>
                {count}
            </Typography>
            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500 }}>
                {label}
            </Typography>
        </Box>
    </Box>
);

// ─── Main Component ────────────────────────────────────────────────────────────
const AllAppointmentsList = () => {
    const [appointments, setAppointments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [integrationStatus, setIntegrationStatus] = useState({ connected: true, loading: true });

    // Pagination and Filtering State
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [statusFilter, setStatusFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    useEffect(() => {
        checkIntegrationStatus();
    }, []);

    // Debounce search term
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchTerm);
            setPage(0); // Reset to first page on new search
        }, 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    // Fetch appointments when page, rowsPerPage, status, or debounced search changes
    useEffect(() => {
        fetchAllAppointments();
    }, [page, rowsPerPage, statusFilter, debouncedSearch]);

    const checkIntegrationStatus = async () => {
        const userId = localStorage.getItem('userId');
        try {
            const [gRes, oRes] = await Promise.all([
                axiosInstance.get('/calendar/status', { params: { userId } }),
                axiosInstance.get('/outlook-calendar/status', { params: { userId } })
            ]);
            setIntegrationStatus({
                connected: gRes.data.isConnected || oRes.data.isConnected,
                loading: false
            });
        } catch (error) {
            setIntegrationStatus({ connected: true, loading: false });
        }
    };

    const fetchAllAppointments = async () => {
        setLoading(true);
        try {
            const userId = localStorage.getItem('userId');
            const res = await axiosInstance.get(`/appointments`, {
                params: {
                    userId,
                    page: page + 1,
                    limit: rowsPerPage,
                    status: statusFilter === 'all' ? undefined : statusFilter,
                    search: debouncedSearch || undefined,
                    sortBy: 'startTime',
                    sortOrder: '-1' // Newest first for list
                }
            });
            if (res.data.success) {
                setAppointments(res.data.appointments);
                setTotalCount(res.data.pagination?.totalCount || 0);
            }
        } catch (error) {
            console.error("Error fetching appointments:", error);
            setSnackbar({ open: true, message: 'Failed to fetch appointments', severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleStatusFilterChange = (event) => {
        setStatusFilter(event.target.value);
        setPage(0);
    };

    const handleCancel = async (appointmentId) => {
        if (!window.confirm("Are you sure you want to cancel this appointment?")) return;
        try {
            await axiosInstance.put(`/appointments/status`, { appointmentId, status: 'cancelled' });
            fetchAllAppointments();
            setSnackbar({ open: true, message: 'Appointment cancelled successfully', severity: 'success' });
        } catch (error) {
            setSnackbar({ open: true, message: 'Failed to cancel appointment', severity: 'error' });
        }
    };

    const handleRemind = async (appointmentId) => {
        try {
            const res = await axiosInstance.post(`/appointments/remind`, { appointmentId });
            if (res.data.success) {
                setSnackbar({ open: true, message: 'Reminder sent successfully', severity: 'success' });
            }
        } catch (error) {
            setSnackbar({ open: true, message: 'Failed to send reminder', severity: 'error' });
        }
    };

    const formatDate = (dateStr) => {
        const d = new Date(dateStr);
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
            + ' · '
            + d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    };

    const getInitials = (name = '') =>
        name.split(' ').slice(0, 2).map(n => n[0]).join('').toUpperCase() || '?';

    const avatarColor = (name = '') => {
        const colors = ['#6366f1', '#0ea5e9', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];
        return colors[(name.charCodeAt(0) || 0) % colors.length];
    };

    const confirmed = appointments.filter(a => a.status === 'confirmed').length;
    const pending = appointments.filter(a => a.status === 'pending').length;
    const cancelled = appointments.filter(a => a.status === 'cancelled').length;

    return (
        <Box>
            {/* Header Row */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5, flexWrap: 'wrap', gap: 1 }}>
                <Box>
                    <Typography variant="h6" fontWeight={800} sx={{ color: '#0f172a', letterSpacing: '-0.3px' }}>
                        All Appointments
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {totalCount} total appointment{totalCount !== 1 ? 's' : ''}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                    {/* Search Field */}
                    <TextField
                        placeholder="Search appointments..."
                        size="small"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        variant="outlined"
                        sx={{
                            width: { xs: '100%', sm: 240 },
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 2.5,
                                bgcolor: '#fff',
                                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#3b82f6' },
                            }
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: '#94a3b8', fontSize: 18 }} />
                                </InputAdornment>
                            ),
                        }}
                    />

                    {/* Status Filter */}
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <Select
                            value={statusFilter}
                            onChange={handleStatusFilterChange}
                            displayEmpty
                            variant="outlined"
                            sx={{
                                borderRadius: 2.5,
                                bgcolor: '#fff',
                                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' },
                                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#3b82f6' },
                                fontWeight: 500,
                                fontSize: '0.875rem'
                            }}
                            startAdornment={
                                <InputAdornment position="start">
                                    <FilterListIcon sx={{ color: '#94a3b8', fontSize: 18 }} />
                                </InputAdornment>
                            }
                        >
                            <MenuItem value="all">All Statuses</MenuItem>
                            <MenuItem value="confirmed">Confirmed</MenuItem>
                            <MenuItem value="pending">Pending</MenuItem>
                            <MenuItem value="cancelled">Cancelled</MenuItem>
                            <MenuItem value="completed">Completed</MenuItem>
                        </Select>
                    </FormControl>

                    <Tooltip title="Refresh appointments" arrow>
                        <IconButton
                            size="small"
                            onClick={() => {
                                setPage(0);
                                fetchAllAppointments();
                            }}
                            disabled={loading}
                            sx={{
                                bgcolor: '#f1f5f9', border: '1px solid #e2e8f0',
                                width: 36, height: 36,
                                '&:hover': { bgcolor: '#e2e8f0' },
                            }}
                        >
                            <RefreshIcon sx={{ fontSize: 18, color: '#475569' }} />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {/* Stats Cards */}
            {!loading && appointments.length > 0 && (
                <Fade in timeout={400}>
                    <Box sx={{ display: 'flex', gap: 2, mb: 2.5, flexWrap: 'wrap' }}>
                        <StatCard label="Confirmed" count={confirmed} color="#16a34a" bg="#f0fdf4"
                            icon={<EventAvailableIcon fontSize="inherit" />} />
                        <StatCard label="Pending" count={pending} color="#d97706" bg="#fffbeb"
                            icon={<HourglassTopIcon fontSize="inherit" />} />
                        <StatCard label="Cancelled" count={cancelled} color="#e11d48" bg="#fff1f2"
                            icon={<CancelIcon fontSize="inherit" />} />
                    </Box>
                </Fade>
            )}

            {/* Calendar not connected banner */}
            {!integrationStatus.loading && !integrationStatus.connected && (
                <Alert
                    severity="info"
                    variant="outlined"
                    sx={{ mb: 2.5, borderRadius: 2.5, fontSize: '0.82rem' }}
                >
                    Calendar not connected.{' '}
                    <strong>Connect Google or Outlook Calendar</strong> in the header to prevent double bookings and enable auto-sync.
                </Alert>
            )}

            {/* Table */}
            <TableContainer
                component={Paper}
                elevation={0}
                sx={{
                    border: '1px solid #e2e8f0',
                    borderRadius: 2.5,
                    overflow: 'hidden',
                }}
            >
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ bgcolor: '#f8fafc' }}>
                            {['Client', 'Email', 'Phone', 'Date & Time', 'Status', 'Actions'].map(h => (
                                <TableCell key={h} sx={{
                                    fontWeight: 700, fontSize: '0.75rem',
                                    color: '#64748b', textTransform: 'uppercase',
                                    letterSpacing: '0.06em', py: 1.5, borderBottom: '1px solid #e2e8f0',
                                }}>
                                    {h}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <SkeletonRows />
                        ) : appointments.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} sx={{ border: 'none', p: 0 }}>
                                    <EmptyState />
                                </TableCell>
                            </TableRow>
                        ) : (
                            appointments.map((appt, idx) => {
                                const name = appt.client?.name || appt.guestDetails?.name || 'Unknown';
                                const email = appt.client?.email || appt.guestDetails?.email || '—';
                                const phone = appt.client?.phone || appt.guestDetails?.phone || '—';
                                const statusCfg = STATUS_CONFIG[appt.status] || STATUS_CONFIG.pending;

                                return (
                                    <TableRow
                                        key={appt._id}
                                        sx={{
                                            bgcolor: idx % 2 === 0 ? '#fff' : '#fafbfc',
                                            transition: 'background 0.15s',
                                            '&:hover': { bgcolor: '#f0f7ff' },
                                            '&:last-child td': { border: 'none' },
                                        }}
                                    >
                                        {/* Client */}
                                        <TableCell sx={{ py: 1.5 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                                                <Avatar
                                                    sx={{
                                                        width: 32, height: 32,
                                                        bgcolor: avatarColor(name),
                                                        fontSize: '0.72rem', fontWeight: 700,
                                                    }}
                                                >
                                                    {getInitials(name)}
                                                </Avatar>
                                                <Typography variant="body2" fontWeight={600} sx={{ color: '#0f172a' }}>
                                                    {name}
                                                </Typography>
                                            </Box>
                                        </TableCell>

                                        {/* Email */}
                                        <TableCell>
                                            <Typography variant="body2" sx={{ color: '#475569', fontSize: '0.8rem' }}>
                                                {email}
                                            </Typography>
                                        </TableCell>

                                        {/* Phone */}
                                        <TableCell>
                                            <Typography variant="body2" sx={{ color: '#475569', fontSize: '0.8rem' }}>
                                                {phone}
                                            </Typography>
                                        </TableCell>

                                        {/* Date & Time */}
                                        <TableCell>
                                            <Typography variant="body2" sx={{ color: '#334155', fontWeight: 500, fontSize: '0.8rem' }}>
                                                {formatDate(appt.startTime)}
                                            </Typography>
                                        </TableCell>

                                        {/* Status */}
                                        <TableCell>
                                            <Chip
                                                label={statusCfg.label}
                                                size="small"
                                                icon={<Box sx={{ color: `${statusCfg.textColor} !important`, ml: '6px !important' }}>{statusCfg.icon}</Box>}
                                                sx={{
                                                    bgcolor: statusCfg.bg,
                                                    color: statusCfg.textColor,
                                                    border: `1px solid ${statusCfg.borderColor}`,
                                                    fontWeight: 600,
                                                    fontSize: '0.7rem',
                                                    height: 24,
                                                    '& .MuiChip-icon': { ml: 0.5 },
                                                }}
                                            />
                                        </TableCell>

                                        {/* Actions */}
                                        <TableCell>
                                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                <Tooltip title="Send reminder" arrow>
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleRemind(appt._id)}
                                                            disabled={appt.status === 'cancelled'}
                                                            sx={{
                                                                color: '#3b82f6',
                                                                bgcolor: '#eff6ff',
                                                                width: 30, height: 30,
                                                                transition: 'all 0.2s',
                                                                '&:hover': { bgcolor: '#dbeafe', transform: 'scale(1.1)' },
                                                                '&.Mui-disabled': { bgcolor: '#f1f5f9', color: '#cbd5e1' },
                                                            }}
                                                        >
                                                            <Notifications sx={{ fontSize: 16 }} />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                                <Tooltip title="Cancel appointment" arrow>
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleCancel(appt._id)}
                                                            disabled={appt.status === 'cancelled'}
                                                            sx={{
                                                                color: '#ef4444',
                                                                bgcolor: '#fff1f2',
                                                                width: 30, height: 30,
                                                                transition: 'all 0.2s',
                                                                '&:hover': { bgcolor: '#fecdd3', transform: 'scale(1.1)' },
                                                                '&.Mui-disabled': { bgcolor: '#f1f5f9', color: '#cbd5e1' },
                                                            }}
                                                        >
                                                            <Delete sx={{ fontSize: 16 }} />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Pagination */}
            {!loading && totalCount > 0 && (
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    component="div"
                    count={totalCount}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    sx={{
                        mt: 1,
                        border: '1px solid #e2e8f0',
                        borderRadius: 2.5,
                        bgcolor: '#f8fafc',
                        '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            color: '#64748b',
                            textTransform: 'uppercase'
                        },
                    }}
                />
            )}

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    severity={snackbar.severity}
                    variant="filled"
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    sx={{ borderRadius: 2, fontWeight: 500 }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default AllAppointmentsList;